<script setup lang="ts">
/**
 * Playlist preview: up to four item thumbnails in a 2×2 mosaic (legacy shows the first item only). With more than
 * four items the last tile shows how many more there are ("+4" for seven items).
 */
import { useI18n } from 'vue-i18n';
import type { Layout, MediaAssetRecord, Playlist } from '@codot/contracts';
import CampaignThumb from './CampaignThumb.vue';
import ZoneWidget from './apps/ZoneWidget.vue';
import { computed } from 'vue';

const { t } = useI18n();

const props = defineProps<{
  playlist: Playlist;
  compact?: boolean;
  assets?: Map<string, MediaAssetRecord>;
  campaigns?: Map<string, Layout>;
}>();
const campaignOf = (tl: Playlist['items'][number]) => (tl.content.kind === 'campaign' ? props.campaigns?.get(tl.content.id) : undefined);
/** A built-in app's config, so its tile shows the app's own (paused) preview. */
const appConfig = (tl: Playlist['items'][number]) => (tl.content.kind === 'media' ? props.assets?.get(tl.content.id)?.config : undefined);
const ICON = { campaign: '▦', app: '🧩', video: '🎬', image: '🖼️', audio: '🎵', pdf: '📄' } as const;
const tiles = computed(() => props.playlist.items.slice(0, 4));
/** Items not shown as their own tile (the 4th tile stands for them). */
const more = computed(() => (props.playlist.items.length > 4 ? props.playlist.items.length - 3 : 0));
</script>

<template>
  <div
    v-if="tiles.length"
    class="grid h-full w-full gap-px bg-slate-900"
    :class="tiles.length > 1 ? 'grid-cols-2' : ''"
  >
    <div
      v-for="tl in tiles"
      :key="tl.id"
      class="relative flex items-center justify-center overflow-hidden bg-slate-800"
      :class="tiles.length === 3 && tl === tiles[0] ? 'row-span-2' : ''"
    >
      <img
        v-if="tl.thumbnailUrl"
        :src="tl.thumbnailUrl"
        alt=""
        class="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
      <CampaignThumb
        v-else-if="assets && campaignOf(tl)"
        :campaign="campaignOf(tl)!"
        :assets="assets"
        class="absolute inset-0"
      />
      <ZoneWidget
        v-else-if="appConfig(tl)"
        :config="appConfig(tl)!"
        :playing="false"
        :width="1920"
        :height="1080"
        class="pointer-events-none absolute inset-0"
      />
      <span v-else :class="compact ? 'text-xs' : 'text-lg'" aria-hidden="true">{{ ICON[tl.contentType] }}</span>
      <span
        v-if="more && tl === tiles[3]"
        class="absolute inset-0 flex items-center justify-center bg-black/60 font-semibold text-white tabular-nums"
        :class="compact ? 'text-[10px]' : 'text-base'"
        :aria-label="t('misc.moreItems', { n: more })"
        >+{{ more }}</span
      >
    </div>
  </div>
  <span v-else class="text-xs text-slate-500">{{ t('misc.emptyPlaylist') }}</span>
</template>
